"use client";

import { type ReactNode, type Ref, useEffect, useRef } from "react";

import { cn } from "../../utils/cn.util";
import { Tooltip, type TooltipContentProps, type TooltipRootProps } from "./Tooltip";

export interface BasicTooltipProps
  extends
    TooltipRootProps,
    Pick<TooltipContentProps, "side" | "align" | "sideOffset" | "alignOffset" | "theme" | "collisionBoundary"> {
  children: ReactNode;
  content: ReactNode;
  className?: string;
  tooltipClassName?: string;
  arrowClassName?: string;
  showWhenOverflow?: boolean;
  asChild?: boolean;
  disabled?: boolean;
  hideArrow?: boolean;
  tooltipContainerClassName?: string;
  ref?: Ref<BasicTooltipProps>;
}

export const BasicTooltip = ({
  ref: _ref,
  children,
  content,
  theme,
  side = "right",
  align = "center",
  className,
  tooltipClassName,
  arrowClassName,
  showWhenOverflow = false,
  sideOffset = 5,
  alignOffset = 0,
  disabled = false,
  asChild = false,
  hideArrow = false,
  defaultOpen,
  open,
  tooltipContainerClassName,
  collisionBoundary,
}: BasicTooltipProps) => {
  const containerRef = useRef<Element | undefined>(undefined);

  useEffect(() => {
    if (!tooltipContainerClassName) {
      containerRef.current = document.body;
      return;
    }
    const nextContainer = document.querySelector(`.${tooltipContainerClassName}`);
    containerRef.current = nextContainer ?? undefined;
  }, [tooltipContainerClassName, containerRef]);

  return (
    <Tooltip.Root defaultOpen={defaultOpen} open={open}>
      <Tooltip.Trigger className={className} onOverflow={showWhenOverflow} asChild={asChild} disabled={disabled}>
        {children}
      </Tooltip.Trigger>
      <Tooltip.Content
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        theme={theme}
        side={side}
        align={align}
        container={containerRef.current}
        collisionBoundary={collisionBoundary}
        className={cn(Boolean(content) && "max-w-[350px] break-words", disabled && "hidden", tooltipClassName)}
      >
        {typeof content === "string" ? <p>{content}</p> : content}
        {hideArrow ? null : <Tooltip.Arrow theme={theme} className={arrowClassName} />}
      </Tooltip.Content>
    </Tooltip.Root>
  );
};

BasicTooltip.displayName = "BasicTooltip";
